import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { COUNTIES, CITIES } from '../data/site';
import { Hero } from '../components/home/Hero';
import { ExpertServices } from '../components/home/ExpertServices';
import { TrustBadges } from '../components/home/TrustSections';
import { ServicesGrid } from '../components/home/ServicesGrid';
import { Testimonials } from '../components/home/GalleryTestimonials';
import { ScheduleCta } from '../components/home/CtaPostsCoupons';

const countyServices = (slug) => [
  { label: 'Roof Installation', path: `/locations/new-jersey/roof-installation-in-${slug}` },
  { label: 'Roof Leak Repair', path: `/locations/new-jersey/roof-leak-repair-in-${slug}` },
  { label: 'Roof Replacement', path: `/locations/new-jersey/roof-replacement-in-${slug}` },
  { label: 'Emergency Roofing', path: `/locations/new-jersey/emergency-roofing-in-${slug}` },
];

export function LocationsNJ() {
  useEffect(() => {
    document.title = 'Locations - New Jersey | Phoenix Roofing & Chimney';
  }, []);
  return (
    <div data-testid="locations-page">
      <Hero titleWhite="Serving" titleRed="New Jersey" kicker="Top Rated Roofing Company" sub="Locally Owned & Operated" />
      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="section-title">Expert Roofing Services in New Jersey</h2>
          <p className="mt-5 text-lg text-foreground/85 max-w-2xl mx-auto">
            Phoenix Roofing & Chimney proudly serves Bergen, Passaic, Hudson, Essex, Morris, Hunterdon, Somerset, Sussex, Union, and Warren Counties.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 mt-12">
            {COUNTIES.map((c) => (
              <Link
                key={c.slug}
                to={`/locations/new-jersey/${c.slug}`}
                className="bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors duration-300 py-7 px-4 flex flex-col items-center font-heading font-semibold"
                data-testid={`county-link-${c.slug}`}
              >
                <MapPin className="w-6 h-6 mb-2" />
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
      <TrustBadges />
      <ScheduleCta />
    </div>
  );
}

export function CountyPage() {
  const { slug } = useParams();
  const data = COUNTIES.find((c) => c.slug === slug);

  useEffect(() => {
    if (data) document.title = `${data.name} Roofing & Chimney Services | Phoenix Roofing & Chimney`;
  }, [data]);

  if (!data) return <Navigate to="/locations/new-jersey" replace />;
  const cities = CITIES[slug] || [];

  return (
    <div data-testid={`county-page-${slug}`}>
      <Hero titleWhite="Roofing & Chimney Services in" titleRed={data.name} kicker="Top Rated Roofing Company" sub="Free Inspections – Same Day Service" />
      <section className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="section-title text-2xl md:text-3xl">Our Services in {data.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {countyServices(slug).map((s) => (
              <Link key={s.label} to={s.path} className="bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors duration-300 py-6 px-4 font-heading font-semibold" data-testid={`county-service-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {s.label}
              </Link>
            ))}
          </div>
          {cities.length > 0 && (
            <>
              <h2 className="section-title text-2xl md:text-3xl mt-16">Cities We Serve in {data.name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
                {cities.map((c) => (
                  <Link
                    key={c.name}
                    to={`/${c.slug}`}
                    className="border border-border hover:border-primary hover:text-primary transition-colors duration-200 py-4 px-3 font-heading"
                    data-testid={`city-link-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {c.name}{c.zip ? `, NJ ${c.zip}` : ', NJ'}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      <ExpertServices title={`Expert Roofing Services in ${data.name}`} intro={`Phoenix Roofing & Chimney proudly serves homeowners across ${data.name}, New Jersey with roof repair, replacement, chimney and gutter services.`} />
      <ServicesGrid />
      <Testimonials />
      <ScheduleCta />
    </div>
  );
}

const titleCase = (s) => s.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export function LocationServicePage() {
  const { slug } = useParams();
  const m = slug && slug.match(/^(.*)-in-(.*)$/);
  const service = m ? titleCase(m[1]) : '';
  const area = m ? titleCase(m[2]) : '';

  useEffect(() => {
    if (service && area) document.title = `${service} in ${area} | Phoenix Roofing & Chimney`;
  }, [service, area]);

  if (!m) return <Navigate to="/locations/new-jersey" replace />;

  return (
    <div data-testid="location-service-page">
      <Hero titleWhite={service} titleRed={`in ${area}`} kicker="Top Rated Roofing Company" sub="Free Inspections – Same Day Service" />
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="section-title text-2xl md:text-3xl">{service} in {area}, New Jersey</h2>
          <p className="mt-6 text-lg text-foreground/85">
            Save Up to 40% on {service.toLowerCase()} with your local roofing and chimney repair specialist serving {area}. Experienced and trusted professionals, high-quality materials and workmanship, free in-house estimates.
          </p>
        </div>
      </section>
      <TrustBadges />
      <ServicesGrid />
      <Testimonials />
      <ScheduleCta />
    </div>
  );
}

export function CityPage() {
  const { slug } = useParams();
  const m = slug && slug.match(/^(.*?)-county-roof-repair-replacement-(.*?)(?:-nj(\d{5}))?$/);
  const county = m ? `${titleCase(m[1])} County` : '';
  const city = m ? titleCase(m[2]) : '';
  const zip = (m && m[3]) || '';

  useEffect(() => {
    if (city) document.title = `${county} Roof Repair & Replacement ${city} NJ | Phoenix Roofing & Chimney`;
  }, [city, county]);

  if (!m) return <Navigate to="/" replace />;

  return (
    <div data-testid="city-page">
      <Hero titleWhite="Roof Repair and Replacement in" titleRed={`${city}, NJ ${zip}`} kicker="Top Rated Roofing Company" sub="Free Inspections" />
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="section-title text-2xl md:text-3xl">Roofing & Chimney Services in {city}, {county}</h2>
          <p className="mt-6 text-lg text-foreground/85">
            Save Up to 40% on Professional Roof Installations and Repairs with Your Local Roofing and Chimney Repair Specialist serving {city}, New Jersey{zip ? ` ${zip}` : ''}.
          </p>
        </div>
      </section>
      <ExpertServices title={`Expert Roofing Services in ${city}, NJ`} intro={`Phoenix Roofing & Chimney proudly serves ${city} and the surrounding ${county} communities.`} />
      <TrustBadges />
      <ServicesGrid />
      <Testimonials />
      <ScheduleCta />
    </div>
  );
}
